import { useState } from 'react';
import { Staff } from '../../types';
import { db } from '../../db';
import { normalizePhone } from '../../src/utils/phoneUtils';

interface SyncQueueItem {
  type: string;
  action: string;
  data: unknown;
  status: string;
  timestamp: number;
}

export function useStaff(
  addToSyncQueue: (item: SyncQueueItem) => Promise<unknown>,
  assertMutationAllowed: (fnName: string) => void,
  currentUser: Staff | null,
  setCurrentUser: (user: Staff | null) => void
) {
  const [staff, setStaff] = useState<Staff[]>([]);

  const addStaff = async (member: Staff) => {
    assertMutationAllowed('addStaff');
    const normalized = { ...member, phone: normalizePhone(member.phone || '') };
    const id = await db.staff.add(normalized);
    const created = { ...normalized, id: id as number };
    await db.syncStack.add({ type: 'staff', action: 'create', data: created, status: 'pending', timestamp: Date.now() });
    setStaff(prev => [...prev, created]);
  };

  const updateStaff = async (member: Staff) => {
    assertMutationAllowed('updateStaff');
    const normalized = { ...member, phone: normalizePhone(member.phone || '') };
    await db.staff.put(normalized);
    await addToSyncQueue({ type: 'staff', action: 'update', data: normalized, status: 'pending', timestamp: Date.now() });
    setStaff(prev => prev.map(s => s.id === normalized.id ? normalized : s));
    if (currentUser && currentUser.id === normalized.id) {
      setCurrentUser(normalized);
    }
  };

  const deleteStaff = async (id: number) => {
    await db.staff.delete(id);
    await db.syncStack.add({ type: 'staff', action: 'delete', data: { id }, status: 'pending', timestamp: Date.now() });
    setStaff(prev => prev.filter(s => s.id !== id));
  };

  return {
    staff,
    setStaff,
    addStaff,
    updateStaff,
    deleteStaff,
  };
}
